import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertTriangle, Users, Search, Calendar, UserCheck, Clock } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

// ═══════════════════════════════════════════════════════════════════════
//  PRÉSENTATION — composants purement visuels
//  Aucune logique métier ni appel API ici : tout arrive par les props.
// ═══════════════════════════════════════════════════════════════════════

const initiales = (nom, prenom) =>
  `${(nom || '').trim().charAt(0)}${(prenom || '').trim().charAt(0)}`.toUpperCase() || '?';

const formatDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('fr-FR');
};

const PaieStyles = () => (
  <style>{`
    @keyframes paieFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
    @keyframes paieModalIn { from { opacity: 0; transform: translateY(18px) scale(.97); } to { opacity: 1; transform: none; } }
    @keyframes paieFade { from { opacity: 0; } to { opacity: 1; } }
    .paie-fade-up { animation: paieFadeUp .38s cubic-bezier(.2,.8,.2,1) both; }
    .paie-modal-in { animation: paieModalIn .3s cubic-bezier(.2,.8,.2,1) both; }
    .paie-fade { animation: paieFade .25s ease-out both; }
    @media (prefers-reduced-motion: reduce) {
      .paie-fade-up, .paie-modal-in, .paie-fade { animation: none; }
    }
  `}</style>
);

// Nombre qui « monte » jusqu'à sa valeur
const AnimatedNumber = ({ value, duration = 900 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const target = Number(value) || 0;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setDisplay(target);
      return undefined;
    }
    let raf;
    let start;
    const step = (t) => {
      if (start === undefined) start = t;
      const k = Math.min(1, (t - start) / duration);
      setDisplay(Math.round(target * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{display.toLocaleString('fr-FR')}</>;
};

// Onglets à indicateur glissant, compteurs et navigation clavier (← →)
const SlidingTabs = ({ tabs, active, onChange, label }) => {
  const listRef = useRef(null);
  const index = Math.max(0, tabs.findIndex((t) => t.id === active));

  const onKeyDown = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = (index + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    onChange(tabs[next].id);
    listRef.current?.querySelectorAll('[role="tab"]')[next]?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="relative grid rounded-xl bg-gray-100 p-1"
      style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
    >
      <span
        aria-hidden="true"
        className="absolute bottom-1 left-1 top-1 rounded-lg bg-white shadow transition-transform duration-300 ease-out"
        style={{ width: `calc((100% - 0.5rem) / ${tabs.length})`, transform: `translateX(${index * 100}%)` }}
      />
      {tabs.map((t) => {
        const Icon = t.icon;
        const selected = t.id === active;
        const danger = t.badgeTone === 'danger';
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.id)}
            className={`relative z-10 flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 ${
              selected ? 'text-nuit' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
            <span className="hidden truncate sm:inline">{t.label}</span>
            {t.badge !== undefined && (
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums transition-colors duration-200 ${
                  danger
                    ? selected ? 'bg-red-100 text-red-700' : 'bg-red-50 text-red-400'
                    : selected ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, text, hint, gradient, progress, delay = 0 }) => {
  // La barre de progression se remplit à l'affichage
  const [on, setOn] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setOn(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
  <div
    className="paie-fade-up relative overflow-hidden rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className={`absolute -right-8 -top-8 h-28 w-28 rounded-full bg-gradient-to-br opacity-10 ${gradient}`} />
    <div className="relative flex items-center gap-4">
      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-lg ${gradient}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
        {text !== undefined ? (
          <div className="truncate text-lg font-bold text-nuit">{text}</div>
        ) : (
          <div className="truncate text-2xl font-bold tabular-nums text-nuit">
            <AnimatedNumber value={value} />
          </div>
        )}
        {hint ? <div className="truncate text-xs text-gray-400">{hint}</div> : null}
      </div>
    </div>
    {typeof progress === 'number' && (
      <div className="relative mt-4 h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${gradient}`}
          style={{ width: on ? `${progress}%` : '0%', transition: 'width 900ms cubic-bezier(.2,.8,.2,1)' }}
        />
      </div>
    )}
  </div>
  );
};

const PersonnelNonIntegre = () => {
  const { typeId } = useParams();
  const navigate = useNavigate();
  const { filialeId, isConsolidated, currentFiliale } = useFiliale();
  const [personnel, setPersonnel] = useState([]);
  const [typePersonnel, setTypePersonnel] = useState(null);
  const [paieEnCours, setPaieEnCours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterContrat, setFilterContrat] = useState('all'); // 'all', 'valide', 'invalide'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [isInterimMode, setIsInterimMode] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Récupérer le type de personnel
      const typesRes = await http.get('/api/types-personnel');
      const allTypes = Array.isArray(typesRes.data) ? typesRes.data : [];
      const typePersonnel = allTypes.find(t => t.idtype_personnel === parseInt(typeId));
      setTypePersonnel(typePersonnel);

      // Déterminer si c'est une paie intérim (basé sur l'activité du type de personnel)
      const isInterim = typePersonnel?.idactivite === 4;
      setIsInterimMode(isInterim);

      // Récupérer la période de paie en cours
      let endpoint = isInterim ? '/api/paies/interim/encours' : '/api/paies/encours';
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        endpoint += `?filiale_id=${filialeId}`;
      }
      const paieRes = await http.get(endpoint).catch(() => ({ data: null }));
      setPaieEnCours(paieRes.data);

      // Récupérer le personnel du type qui n'est pas encore intégré
      const personnelRes = await http.get('/api/personnel');
      const allPersonnel = Array.isArray(personnelRes.data?.data) ? personnelRes.data.data : [];
      
      // Filtrer par type de personnel
      let filteredPersonnel = allPersonnel.filter(p => p.type_personnel_id === parseInt(typeId) && p.active === 1);
      
      // Filtrage par filiale
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        filteredPersonnel = filteredPersonnel.filter(p => p.filiale_id === parseInt(filialeId));
      }

      // Récupérer les IDs du personnel déjà intégré dans paie_element ou paie_element_interim
      if (paieRes.data) {
        const paieId = isInterim ? paieRes.data.idpaie_interime : paieRes.data.idpaie;
        try {
          const integresRes = await http.get(`/api/paie-element?paie_id=${paieId}&type_personnel_id=${typeId}&isInterim=${isInterim}`);
          const integreIds = Array.isArray(integresRes.data) ? integresRes.data.map(p => p.personnel_id) : [];
          
          // Filtrer pour ne garder que les non intégrés
          filteredPersonnel = filteredPersonnel.filter(p => !integreIds.includes(p.idpersonnel));
        } catch (error) {
          console.error('Erreur récupération paie_element:', error);
          // En cas d'erreur, on affiche tout le personnel
        }
      }

      setPersonnel(filteredPersonnel);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, filialeId, isConsolidated, currentFiliale]);

  // Revenir à la première page quand la recherche ou l'onglet change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterContrat]);

  const handleIntegrer = async () => {
    try {
      // Intégrer tous les employés non intégrés (filtrés) avec calculs complets
      if (!paieEnCours) {
        alert('Merci d\'ouvrir une nouvelle période de paie');
        return;
      }

      let integres = 0;
      let ignores = 0;

      const paieId = isInterimMode ? paieEnCours.idpaie_interime : paieEnCours.idpaie;

      for (const p of filteredPersonnel) {
        const contratStatus = checkContratValide(p);
        if (!contratStatus.valide) {
          ignores++;
          continue;
        }

        await http.post('/api/paie-element', {
          personnel_id: p.idpersonnel,
          paie_id: paieId,
          idtypepersonnel: parseInt(typeId),
          isInterim: isInterimMode,
          withCalculs: true // Activer les calculs complets
        });
        integres++;
      }

      alert(`${integres} employé(s) intégré(s) avec succès${ignores > 0 ? `. ${ignores} employé(s) ignoré(s) car contrat invalide` : ''}`);
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Erreur intégration:', error);
      alert('Erreur lors de l\'intégration');
    }
  };

  const handleIntegrerIndividuel = async (employe) => {
    try {
      if (!paieEnCours) {
        alert('Merci d\'ouvrir une nouvelle période de paie');
        return;
      }

      const paieId = isInterimMode ? paieEnCours.idpaie_interime : paieEnCours.idpaie;

      await http.post('/api/paie-element', {
        personnel_id: employe.idpersonnel,
        paie_id: paieId,
        idtypepersonnel: parseInt(typeId),
        isInterim: isInterimMode,
        withCalculs: true // Activer les calculs complets
      });

      alert(`${employe.nom} ${employe.prenom} intégré avec succès`);
      loadData();
    } catch (error) {
      console.error('Erreur intégration individuelle:', error);
      alert('Erreur lors de l\'intégration');
    }
  };

  // Vérifier la validité du contrat pour chaque employé
  const checkContratValide = (employe) => {
    // Si pas de contrat
    if (!employe.contrat_nom) {
      return { valide: false, message: 'Pas de contrat' };
    }

    // Si c'est un CDI (type_contrat_id == 3), toujours valide
    if (employe.type_contrat_id === 3) {
      return { valide: true, message: 'CDI' };
    }

    // Pour les autres contrats (CDD, Stage, etc.), vérifier les dates
    if (!paieEnCours || !paieEnCours.date_debut || !paieEnCours.date_fin) {
      return { valide: true, message: employe.contrat_nom };
    }

    const dateDebutPaie = new Date(paieEnCours.date_debut);
    const dateFinPaie = new Date(paieEnCours.date_fin);
    
    // Si on a les dates de contrat
    if (employe.date_entree_contrat && employe.date_fin_contrat) {
      const dateDebutContrat = new Date(employe.date_entree_contrat);
      const dateFinContrat = new Date(employe.date_fin_contrat);

      // Vérifier si le contrat couvre la période de paie
      // Le contrat doit être valide pendant toute la période de paie
      if (dateFinContrat < dateDebutPaie) {
        return { valide: false, message: 'Contrat expiré avant la période de paie' };
      }

      if (dateDebutContrat > dateFinPaie) {
        return { valide: false, message: 'Contrat commence après la période de paie' };
      }

      return { valide: true, message: employe.contrat_nom };
    }

    // Si pas de dates de contrat, on considère comme valide pour l'instant
    return { valide: true, message: employe.contrat_nom };
  };

  const filteredPersonnel = personnel.filter(p => {
    const matchSearch = p.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        p.prenom.toLowerCase().includes(searchTerm.toLowerCase());
    
    const contratStatus = checkContratValide(p);
    let matchContrat = true;
    
    if (filterContrat === 'valide') {
      matchContrat = contratStatus.valide;
    } else if (filterContrat === 'invalide') {
      matchContrat = !contratStatus.valide;
    }
    
    return matchSearch && matchContrat;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPersonnel.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPersonnel.length / itemsPerPage);

  // Compter seulement les employés avec contrat valide
  const personnelValide = filteredPersonnel.filter(p => checkContratValide(p).valide);
  const nombreIntegrable = personnelValide.length;

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Valeurs dérivées pour l'affichage (lecture seule de l'état existant)
  const nbValides = personnel.filter((p) => checkContratValide(p).valide).length;
  const nbInvalides = personnel.length - nbValides;
  const tauxValides = personnel.length ? Math.round((nbValides / personnel.length) * 100) : 0;
  const debutPaie = formatDate(paieEnCours?.date_debut);
  const finPaie = formatDate(paieEnCours?.date_fin);
  const periodeTexte = debutPaie && finPaie ? `${debutPaie} → ${finPaie}` : undefined;
  // Les onglets pilotent le même état que l'ancien <select> : 'all' | 'valide' | 'invalide'
  const ongletsContrat = [
    { id: 'all', label: 'Tous les contrats', icon: Users, badge: personnel.length },
    { id: 'valide', label: 'Contrats valides', icon: CheckCircle, badge: nbValides },
    { id: 'invalide', label: 'Contrats invalides', icon: AlertTriangle, badge: nbInvalides, badgeTone: 'danger' },
  ];

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PaieStyles />
      <BlueHeader
        title="Personnels Non Intégrés"
        subtitle={typePersonnel?.libelle || 'Chargement...'}
        showBack={true}
        onBack={() => navigate(-1)}
        actions={[
          {
            label: 'Intégrer et valider les heures',
            icon: CheckCircle,
            onClick: () => setShowModal(true),
            primary: true,
            disabled: nombreIntegrable === 0
          }
        ]}
      />

      <div className="px-6 py-8">
        {nombreIntegrable > 0 && (
          <div className="paie-fade-up mb-6 flex items-center gap-3 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 p-4">
            <span className="relative flex h-3 w-3 flex-shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 motion-safe:animate-ping" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-amber-500" />
            </span>
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-yellow-600" />
            <p className="text-yellow-800">
              {typePersonnel?.libelle} NON INTÉGRÉ - {nombreIntegrable} agent(s) à intégrer
            </p>
          </div>
        )}

        {/* Indicateurs clés */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard icon={Users} label="Non intégrés" value={personnel.length} gradient="from-blue-500 to-indigo-600" delay={0} />
          <StatCard
            icon={UserCheck}
            label="Intégrables"
            value={nbValides}
            hint={personnel.length ? `${tauxValides}% des agents` : undefined}
            progress={tauxValides}
            gradient="from-emerald-500 to-teal-600"
            delay={60}
          />
          <StatCard icon={AlertTriangle} label="Contrats invalides" value={nbInvalides} hint={nbInvalides > 0 ? 'À régulariser avant intégration' : undefined} gradient="from-rose-500 to-red-600" delay={120} />
          <StatCard
            icon={Calendar}
            label="Période de paie"
            text={paieEnCours?.libelle_paie || 'Aucune période ouverte'}
            hint={periodeTexte}
            gradient="from-fuchsia-500 to-purple-600"
            delay={180}
          />
        </div>

        <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="space-y-4 border-b border-gray-100 p-4 sm:p-6">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Liste du personnel non intégré</h3>
                <p className="mt-1 text-sm text-gray-500">{filteredPersonnel.length} employé(s) ({nombreIntegrable} intégrable(s))</p>
              </div>
              <div className="relative lg:w-80">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom ou prénom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-10 pr-4 text-sm transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>
            <div className="max-w-2xl">
              <SlidingTabs tabs={ongletsContrat} active={filterContrat} onChange={setFilterContrat} label="Filtre par validité du contrat" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/80">
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Nom</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Prénom</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Remarque</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody key={filterContrat} className="divide-y divide-gray-100">
                {filteredPersonnel.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                        {personnel.length === 0 ? <CheckCircle className="h-6 w-6 text-emerald-500" /> : <Search className="h-6 w-6 text-gray-400" />}
                      </div>
                      <p className="text-gray-500">Aucun employé non intégré trouvé</p>
                    </td>
                  </tr>
                ) : (
                  currentItems.map((p, i) => {
                    const contratStatus = checkContratValide(p);
                    return (
                      <tr
                        key={p.idpersonnel}
                        className="paie-fade group transition-colors hover:bg-blue-50/40"
                        style={{ animationDelay: `${Math.min(i, 14) * 22}ms` }}
                      >
                        <td className="px-6 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white shadow-sm ${
                              contratStatus.valide ? 'bg-gradient-to-br from-blue-500 to-indigo-600' : 'bg-gradient-to-br from-gray-400 to-gray-500'
                            }`}>
                              {initiales(p.nom, p.prenom)}
                            </div>
                            <span className="text-sm font-medium text-gray-900">{p.nom}</span>
                          </div>
                        </td>
                        <td className="px-6 py-3.5 text-sm text-gray-600">{p.prenom}</td>
                        <td className="px-6 py-3.5">
                          {contratStatus.valide ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-green-600 ring-1 ring-emerald-100">
                                <CheckCircle className="h-4 w-4" /> Contrat valide
                              </span>
                              {p.contrat_nom && (
                                <span className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">{p.contrat_nom}</span>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-sm font-medium text-red-600 ring-1 ring-red-100">
                              <AlertTriangle className="h-4 w-4" /> {contratStatus.message}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3.5 text-center">
                          <button
                            onClick={() => handleIntegrerIndividuel(p)}
                            disabled={!contratStatus.valide}
                            className={`mx-auto inline-flex items-center gap-1.5 rounded-xl px-4 py-1.5 text-sm font-medium text-white shadow-sm transition-all duration-200 ${
                              contratStatus.valide
                                ? 'bg-gradient-to-r from-emerald-500 to-green-600 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0'
                                : 'cursor-not-allowed bg-gray-300'
                            }`}
                          >
                            <CheckCircle className="h-4 w-4" /> Intégrer
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredPersonnel.length > itemsPerPage && (
            <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/60 px-6 py-4 sm:flex-row">
              <div className="text-sm text-gray-600">
                Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredPersonnel.length)} sur {filteredPersonnel.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Précédent
                </button>
                <span className="px-2 text-sm text-gray-600">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmation */}
      {showModal && (
        <div className="paie-fade fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="paie-modal-in w-full max-w-md overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-2xl">
            <div className="relative overflow-hidden bg-gradient-bleu px-6 py-5 text-white">
              <div className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
              <div className="relative flex items-center gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/30">
                  <Clock className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold">Validation des heures par défaut</h3>
              </div>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600">
                Veuillez vérifier que tout le personnel administratif indirect est intégré avant validation. Les éléments suivants seront validés par défaut pour tout le personnel :
              </p>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 p-3 ring-1 ring-blue-100">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Heures de présence</div>
                  <div className="mt-1 text-xl font-bold tabular-nums text-nuit">{paieEnCours?.heure_tps_plein || 0}</div>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 p-3 ring-1 ring-blue-100">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Heures fériées</div>
                  <div className="mt-1 text-xl font-bold tabular-nums text-nuit">{paieEnCours?.heure_ferie_mois || 0}</div>
                </div>
              </div>

              <div className="mt-3 divide-y divide-gray-100 rounded-xl border border-gray-100 text-sm text-gray-700">
                {[
                  "Heures d'absence non justifiées",
                  "Heures d'absence maladie",
                  'Heures programmées',
                  'Taux de présence',
                  'Heures à payer',
                ].map((libelle) => (
                  <div key={libelle} className="flex justify-between px-3 py-2">
                    <span className="font-medium">{libelle}</span>
                    <span className="tabular-nums text-gray-500">0</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center gap-3 rounded-xl bg-emerald-50 p-3 ring-1 ring-emerald-100">
                <UserCheck className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                <p className="text-sm text-emerald-800">
                  <span className="font-bold tabular-nums">{nombreIntegrable}</span> agent(s) seront intégrés
                  {filteredPersonnel.length - nombreIntegrable > 0 && (
                    <>, <span className="font-bold tabular-nums">{filteredPersonnel.length - nombreIntegrable}</span> ignoré(s) (contrat invalide)</>
                  )}
                </p>
              </div>

              <div className="mt-3 flex items-start gap-3 rounded-xl border border-yellow-200 bg-yellow-50 p-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600" />
                <p className="text-sm text-yellow-800">
                  Voulez-vous vraiment continuer? Cette action est irréversible.
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="rounded-xl bg-gray-100 px-5 py-2 text-gray-700 transition-colors hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  onClick={handleIntegrer}
                  className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2 font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md active:translate-y-0"
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonnelNonIntegre;